// src/constants/regionTiers.js
// Tramos de distancia desde Getxo y precios base 2026 (sin IVA).
// El CRM trabaja con provincia/region, no con kilometros exactos por venue.

/**
 * Definicion de tramos.
 */
export const TIER_DEFINITIONS = {
    1: {
        label: "En Vizcaya",
        distance: "0 km",
        basePrice: 5350,
        productionIncluded: true,
        productionLabel: "Incluye sonido, tarima y luces",
        cachePrice: 4500,
        providersPrice: 850,
        // A diferencia del resto de tramos, aqui el tecnico NO esta dentro de
        // proveedores (no lo paga un proveedor externo con dinero del cliente):
        // sale de nuestro propio cache, y es un gasto propio, no de proveedores.
        technicianCost: 400,
    },
    2: {
        label: "Fuera de Vizcaya hasta 150 km",
        distance: "<= 150 km",
        basePrice: 6000,
        productionIncluded: true,
        productionLabel: "Incluye sonido, tarima y luces",
        cachePrice: 5000,
        providersPrice: 1400,
    },
    3: {
        label: "Fuera de Vizcaya de 150 a 300 km",
        distance: "150-300 km",
        basePrice: 7050,
        productionIncluded: true,
        productionLabel: "Incluye sonido, tarima y luces",
        cachePrice: 5500,
        providersPrice: 1750,
    },
    4: {
        label: "Fuera de Vizcaya de 300 a 600 km",
        distance: "300-600 km",
        basePrice: 5700,
        productionIncluded: false,
        productionLabel: "No incluye sonido, tarima ni luces",
        cachePrice: 5700,
        providersPrice: 2250,
    },
    5: {
        label: "Fuera de Vizcaya de 300 a 600 km (Madrid y alrededores)",
        distance: "300-600 km",
        basePrice: 7650,
        productionIncluded: true,
        productionLabel: "Incluye sonido, tarima y luces",
        cachePrice: 6400,
        providersPrice: 1250,
    },
    6: {
        label: "Fuera de Vizcaya a mas de 600 km",
        distance: "+ 600 km",
        basePrice: 6500,
        productionIncluded: false,
        productionLabel: "No incluye sonido, tarima ni luces",
        cachePrice: 6500,
        providersPrice: null, // a consultar: sin coste de proveedores de referencia
    },
};

/**
 * Mapa region -> tier.
 */
const REGION_TO_TIER = {
    // Tramo 1: En Vizcaya
    "bizkaia": 1,

    // Tramo 2: fuera de Vizcaya hasta 150 km
    "araba": 2,
    "cantabria": 2,
    "gipuzkoa": 2,
    "la rioja": 2,
    "navarra": 2,

    // Tramo 3: fuera de Vizcaya de 150 a 300 km
    "asturias": 3,
    "burgos": 3,
    "huesca": 3,
    "leon": 3,
    "palencia": 3,
    "soria": 3,
    "valladolid": 3,
    "zaragoza": 3,

    // Tramo 4: fuera de Vizcaya de 300 a 600 km, sin produccion incluida
    "a coruna": 4,
    "caceres": 4,
    "castellon": 4,
    "cuenca": 4,
    "girona": 4,
    "lleida": 4,
    "lugo": 4,
    "ourense": 4,
    "pontevedra": 4,
    "salamanca": 4,
    "tarragona": 4,
    "teruel": 4,
    "valencia": 4,
    "zamora": 4,

    // Tramo 5: cerca de Madrid, con produccion incluida
    "albacete": 5,
    "avila": 5,
    "ciudad real": 5,
    "guadalajara": 5,
    "madrid": 5,
    "segovia": 5,
    "toledo": 5,

    // Tramo 6: fuera de Vizcaya a mas de 600 km, sin produccion incluida
    "alicante": 6,
    "almeria": 6,
    "badajoz": 6,
    "baleares": 6,
    // Barcelona esta forzada al tramo 6 (+600 km) aunque su distancia real no
    // llegue a esa franja: decision comercial para que la sugerencia de
    // precio en Barcelona sea siempre la mas alta (6.500 EUR + IVA) en vez de
    // la del tramo 4 (5.700 EUR + IVA).
    "barcelona": 6,
    "cadiz": 6,
    "ceuta": 6,
    "cordoba": 6,
    "granada": 6,
    "huelva": 6,
    "jaen": 6,
    "las palmas": 6,
    "malaga": 6,
    "melilla": 6,
    "murcia": 6,
    "santa cruz de tenerife": 6,
    "sevilla": 6,
};

/**
 * Obtiene el numero de tramo para una region.
 * @param {string} region - Region normalizada (lowercase)
 * @returns {number|null}
 */
export function getTierForRegion(region) {
    if (!region) return null;
    return REGION_TO_TIER[region] ?? null;
}

/**
 * Obtiene el precio base (sin IVA) para una region.
 * @param {string} region - Region normalizada
 * @returns {number|null}
 */
export function getBasePriceForRegion(region) {
    const tier = getTierForRegion(region);
    if (!tier) return null;
    return TIER_DEFINITIONS[tier]?.basePrice ?? null;
}

/**
 * Coste de referencia de proveedores (equipo: sonido, tarima, luces) para el
 * tramo de una region. Solo se debe restar del margen cuando el evento tiene
 * equipment_included = true. Null si no hay precio de referencia (a consultar).
 * @param {string} region - Region normalizada
 * @returns {number|null}
 */
export function getProvidersCostForRegion(region) {
    const tier = getTierForRegion(region);
    if (!tier) return null;
    return TIER_DEFINITIONS[tier]?.providersPrice ?? null;
}

/**
 * Coste de tecnico propio para el tramo de una region. Economicamente sale
 * de nuestro propio cache (no se lo pagamos a un proveedor externo), pero a
 * efectos de las analiticas de Finanzas se repercute igual que un pago a
 * proveedor (ver getProvidersCost en Finance.js), no como gasto propio, para
 * tratar todos los tramos de la misma forma. Solo aplica hoy al tramo 1
 * (Vizcaya); el resto de tramos ya incluyen el tecnico dentro de
 * providersPrice. 0 si el tramo no tiene este coste separado.
 * @param {string} region - Region normalizada
 * @returns {number}
 */
export function getTechnicianCostForRegion(region) {
    const tier = getTierForRegion(region);
    if (!tier) return 0;
    return TIER_DEFINITIONS[tier]?.technicianCost ?? 0;
}

/**
 * Resuelve si un evento incluye equipo (proveedores) para efectos de coste:
 * respeta event.equipment_included si esta decidido explicitamente (true o
 * false), y si no (null/undefined, evento aun no decidido, tipico en
 * presupuestos/pipeline) usa el valor por defecto del tramo de su region
 * (tierInfo.productionIncluded). Sin esto, un lead sin decidir se trataba
 * como "sin proveedores" aunque su tramo los incluya casi siempre por
 * defecto (p.ej. Vizcaya), inflando el margen mostrado.
 * @param {{equipment_included?: boolean|null}} event
 * @param {{productionIncluded?: boolean}|null} tierInfo - resultado de getTierInfoForRegion
 * @returns {boolean}
 */
export function resolveEquipmentIncluded(event, tierInfo) {
    if (event?.equipment_included === true || event?.equipment_included === false) {
        return event.equipment_included;
    }
    return Boolean(tierInfo?.productionIncluded);
}

/**
 * Obtiene la definicion completa del tramo para una region.
 * @param {string} region
 * @returns {{ tier: number, label: string, distance: string, basePrice: number, productionIncluded: boolean, productionLabel: string }|null}
 */
export function getTierInfoForRegion(region) {
    const tier = getTierForRegion(region);
    if (!tier) return null;
    const def = TIER_DEFINITIONS[tier];
    return def ? { tier, ...def } : null;
}

/**
 * Devuelve todas las regiones que pertenecen al mismo tramo que la region dada.
 * @param {string} region
 * @returns {string[]}
 */
export function getRegionsInSameTier(region) {
    const tier = getTierForRegion(region);
    if (!tier) return [];
    return Object.entries(REGION_TO_TIER)
        .filter(([, t]) => t === tier)
        .map(([r]) => r);
}
